import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Box,
	CircularProgress,
} from '@mui/material';
import { Cancel, Save } from '@mui/icons-material';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

export interface SystemNotificationRecord {
	_id: string;
	name: string;
	eventType: string;
	message: string;
	channel: 'email' | 'gsm' | 'whatsapp';
	status: 'active' | 'inactive';
	createdBy?: string;
	updatedBy?: string;
	createdAt?: string;
	updatedAt?: string;
}

interface PlatformEventType {
	value: string;
	description: string;
}

interface Props {
	open: boolean;
	onClose: () => void;
	onSave: () => void;
	notification: SystemNotificationRecord | null;
}

const CHANNEL_OPTIONS: { value: SystemNotificationRecord['channel']; label: string }[] = [
	{ value: 'email', label: 'Email' },
	{ value: 'gsm', label: 'GSM Text' },
	{ value: 'whatsapp', label: 'WhatsApp' },
];

const MESSAGE_VARIABLES_HELP =
	'You can use variables in the message: {{auditEventName}}, {{auditEventDescription}}, {{user.firstName}}, {{user.lastName}}';

const EMPTY_FORM = {
	name: '',
	eventType: '',
	message: '',
	channel: 'email' as SystemNotificationRecord['channel'],
	status: 'active' as SystemNotificationRecord['status'],
};

export const SystemNotificationDialog: React.FC<Props> = ({ open, onClose, onSave, notification }) => {
	const isEditing = Boolean(notification?._id);
	const [form, setForm] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);
	const [eventTypes, setEventTypes] = useState<PlatformEventType[]>([]);
	const [loadingEventTypes, setLoadingEventTypes] = useState(false);

	useEffect(() => {
		if (!open) return;
		if (notification) {
			setForm({
				name: notification.name || '',
				eventType: notification.eventType || '',
				message: notification.message || '',
				channel: notification.channel || 'email',
				status: notification.status || 'active',
			});
		} else {
			setForm(EMPTY_FORM);
		}
		setErrors({});
	}, [open, notification]);

	useEffect(() => {
		if (!open) return;
		const loadEventTypes = async () => {
			setLoadingEventTypes(true);
			try {
				const response = await apiClient.get('/system-notifications/event-types');
				setEventTypes(Array.isArray(response.data) ? response.data : []);
			} catch (error) {
				Logger.error('SystemNotificationDialog', `Failed to load event types: ${error}`);
				setEventTypes([]);
			} finally {
				setLoadingEventTypes(false);
			}
		};
		loadEventTypes();
	}, [open]);

	const validate = (): boolean => {
		const next: Record<string, string> = {};
		if (!form.name.trim()) next.name = 'Name is required';
		if (!form.eventType.trim()) next.eventType = 'Event Type is required';
		if (!form.message.trim()) next.message = 'Message is required';
		if (!form.channel) next.channel = 'Notification Channel is required';
		setErrors(next);
		return Object.keys(next).length === 0;
	};

	const handleSave = async () => {
		if (!validate()) return;
		setSaving(true);
		try {
			const payload = {
				name: form.name.trim(),
				eventType: form.eventType.trim(),
				message: form.message,
				channel: form.channel,
				status: form.status,
			};
			if (isEditing && notification?._id) {
				await apiClient.put(`/system-notifications/${notification._id}`, payload);
			} else {
				await apiClient.post('/system-notifications/new', payload);
			}
			onSave();
		} catch (error) {
			Logger.error('SystemNotificationDialog', `Failed to save notification: ${error}`);
			setErrors({ form: error instanceof Error ? error.message : 'Failed to save notification' });
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>{isEditing ? 'Edit Notification' : 'New Notification'}</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={2} pt={1}>
					{errors.form && (
						<Typography color="error" variant="body2">{errors.form}</Typography>
					)}
					<TextField
						label="Name"
						value={form.name}
						onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
						error={Boolean(errors.name)}
						helperText={errors.name}
						fullWidth
						required
						size="small"
					/>
					<FormControl fullWidth size="small" error={Boolean(errors.eventType)} required>
						<InputLabel>Event Type</InputLabel>
						<Select
							label="Event Type"
							value={form.eventType}
							onChange={(e) => setForm((prev) => ({ ...prev, eventType: e.target.value as string }))}
							disabled={loadingEventTypes}
						>
							{loadingEventTypes && (
								<MenuItem value="" disabled>
									Loading…
								</MenuItem>
							)}
							{eventTypes.map((et) => (
								<MenuItem key={et.value} value={et.value}>
									{et.value}
								</MenuItem>
							))}
						</Select>
						{errors.eventType && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
								{errors.eventType}
							</Typography>
						)}
						{form.eventType && (
							<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
								{(eventTypes.find((e) => e.value === form.eventType)?.description) || ''}
							</Typography>
						)}
					</FormControl>
					<TextField
						label="Message"
						value={form.message}
						onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
						error={Boolean(errors.message)}
						helperText={errors.message || MESSAGE_VARIABLES_HELP}
						fullWidth
						required
						multiline
						minRows={4}
						size="small"
					/>
					<FormControl fullWidth size="small" error={Boolean(errors.channel)} required>
						<InputLabel>Notification Channel</InputLabel>
						<Select
							label="Notification Channel"
							value={form.channel}
							onChange={(e) =>
								setForm((prev) => ({
									...prev,
									channel: e.target.value as SystemNotificationRecord['channel'],
								}))
							}
						>
							{CHANNEL_OPTIONS.map((opt) => (
								<MenuItem key={opt.value} value={opt.value}>
									{opt.label}
								</MenuItem>
							))}
						</Select>
						{errors.channel && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
								{errors.channel}
							</Typography>
						)}
					</FormControl>
					{isEditing && (
						<FormControl fullWidth size="small">
							<InputLabel>Status</InputLabel>
							<Select
								label="Status"
								value={form.status}
								onChange={(e) =>
									setForm((prev) => ({
										...prev,
										status: e.target.value as SystemNotificationRecord['status'],
									}))
								}
							>
								<MenuItem value="active">Active</MenuItem>
								<MenuItem value="inactive">Inactive</MenuItem>
							</Select>
						</FormControl>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					color="secondary"
					onClick={onClose}
					disabled={saving}
					startIcon={<Cancel />}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSave}
					startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
					variant="contained"
					color="success"
					disabled={saving || loadingEventTypes}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default SystemNotificationDialog;
